import { useState } from 'react'
import { LogOut, X, Plus, Trash2 } from 'lucide-react'
import type { GoalItem } from '@/api/attendance'

/** Check-out modal: optional explicit time (needs approval), goal review
 * (mark complete / already completed), and extra completed items. Extracted
 * from the "My Attendance" page so it can also be mounted from the HR
 * dashboard. */
export default function CheckoutModal({
  todaysGoals,
  onSubmit,
  onClose,
  isPending,
  staleDate,
  staleDefaultTime,
  error,
}: {
  todaysGoals: GoalItem[]
  onSubmit: (completedIds: string[], additional: string[], atTime?: string, reason?: string) => void
  onClose: () => void
  isPending: boolean
  /** Set when the open session was forgotten on an earlier day: the user must
   * give the time they left that day and a reason (sent for approval). */
  staleDate?: string | null
  staleDefaultTime?: string | null
  error?: string
}) {
  const [completedIds, setCompletedIds] = useState<Set<string>>(
    () => new Set(todaysGoals.filter((g) => g.is_completed).map((g) => g.id))
  )
  const [additional, setAdditional] = useState<string[]>([''])
  const [atTime, setAtTime] = useState(staleDate ? staleDefaultTime || '' : '')
  const [reason, setReason] = useState('')
  const staleLabel = staleDate
    ? new Date(`${staleDate}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
    : ''
  const canSubmit = !staleDate || (!!atTime && !!reason.trim())

  const toggleGoal = (id: string) => {
    const next = new Set(completedIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setCompletedIds(next)
  }

  const addRow = () => setAdditional([...additional, ''])
  const removeRow = (i: number) => setAdditional(additional.filter((_, idx) => idx !== i))
  const updateRow = (i: number, val: string) => {
    const copy = [...additional]
    copy[i] = val
    setAdditional(copy)
  }

  const handleSubmit = () => {
    const extras = additional.map((a) => a.trim()).filter(Boolean)
    onSubmit(Array.from(completedIds), extras, atTime || undefined, reason.trim() || undefined)
  }

  const incompleteGoals = todaysGoals.filter((g) => !g.is_completed)
  const alreadyCompleted = todaysGoals.filter((g) => g.is_completed)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{staleDate ? `Check Out for ${staleLabel}` : 'Check Out'}</h2>
            <p className="text-sm text-gray-500">{staleDate ? 'You forgot to check out on this day' : 'Review your goals for today'}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="h-5 w-5 text-gray-500" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {error && <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-600">{error}</div>}

          {staleDate ? (
            /* Late check-out for a forgotten day: time + reason are required */
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 space-y-3">
              <p className="text-sm text-amber-800">
                You're still checked in from <strong>{staleLabel}</strong>. Enter the time you actually left and why
                you're checking out late. You'll be checked out right away, and admin/HR will review it.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time you left on {staleLabel}</label>
                <input
                  type="time"
                  value={atTime}
                  onChange={(e) => setAtTime(e.target.value)}
                  className="w-full sm:w-40 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#EC5B2C]/40 focus:border-[#EC5B2C]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Why are you checking out late?</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  placeholder="e.g. Forgot to check out before leaving"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#EC5B2C]/40 focus:border-[#EC5B2C]"
                />
              </div>
            </div>
          ) : (
          /* Optional check-out time */
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Check-out time <span className="font-normal text-gray-400">(optional)</span></label>
            <input
              type="time"
              value={atTime}
              onChange={(e) => setAtTime(e.target.value)}
              className="w-full sm:w-40 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#EC5B2C]/40 focus:border-[#EC5B2C]"
            />
            <p className="text-xs text-gray-400 mt-1">Leave blank to check out now.</p>
            {atTime && (
              <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3">
                <p className="text-xs text-amber-700">
                  A check-out at a specific time needs an admin/manager's approval before it's logged.
                </p>
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason (optional)"
                  className="mt-2 w-full px-3 py-2 border border-amber-200 rounded-lg text-sm focus:ring-2 focus:ring-[#EC5B2C]/40 focus:border-[#EC5B2C]"
                />
              </div>
            )}
          </div>
          )}

          {/* Goals to mark complete */}
          {incompleteGoals.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Mark completed goals</h3>
              <div className="space-y-2">
                {incompleteGoals.map((g) => (
                  <label key={g.id} className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={completedIds.has(g.id)}
                      onChange={() => toggleGoal(g.id)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-900">{g.description}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Already completed */}
          {alreadyCompleted.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Already completed</h3>
              <div className="space-y-2">
                {alreadyCompleted.map((g) => (
                  <div key={g.id} className="flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-200">
                    <span className="text-green-600 text-sm">&#10003;</span>
                    <span className="text-sm text-green-800 line-through">{g.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {todaysGoals.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">No goals set for today</p>
          )}

          {/* Additional completed items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-700">Additional completed items</h3>
              <button onClick={addRow} className="flex items-center gap-1 text-xs text-brand hover:text-brand-dark">
                <Plus className="h-3 w-3" /> Add
              </button>
            </div>
            <div className="space-y-2">
              {additional.map((val, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={val}
                    onChange={(e) => updateRow(i, e.target.value)}
                    placeholder="Something else you completed..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRow() } }}
                  />
                  {additional.length > 1 && (
                    <button onClick={() => removeRow(i)} className="p-1.5 text-gray-400 hover:text-red-500">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="p-5 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending || !canSubmit}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            <LogOut className="h-4 w-4" />
            {isPending ? 'Checking out...' : 'Check Out'}
          </button>
        </div>
      </div>
    </div>
  )
}
